/**
 * Toggle Switch Button control is a custom HTML5 input-type checkbox control that allows you to perform a toggle (on/off) action between checked and unchecked states. It supports different sizes, labels, label positions, and UI customization.
 *
 * @module views/Atoms/CheckboxToggle
 * @memberof -Common
 */
import './CheckboxToggle.scss';

import React, { useCallback } from 'react';

import { useField } from 'formik';
import PropTypes from 'prop-types';
import { Checkbox as PalCheckbox } from 'web-palette/dist/components/Checkbox/Checkbox';

import ResponseMessages from '@ulta/core/components/ResponseMessages/ResponseMessages';
import { triggerDatacaptureEvent } from '@ulta/core/utils/datacapture/datacapture';

import * as utils from './CheckboxToggle';

/**
 * Represents a CheckboxToggle component
 *
 * @method
 * @param {CheckboxToggleProps} props - React properties passed from composition
 * @returns CheckboxToggle
 */
const CheckboxToggle = React.forwardRef( ( props, ref ) => {
  const {
    ariaDescribedBy,
    ariaLabel,
    ariaLabelledBy,
    checked,
    children,
    className,
    count,
    dataCaptureData,
    defaultChecked,
    description,
    disabled,
    error,
    id,
    isFullyClickable = defaultProps.isFullyClickable,
    labelPositionLeft = defaultProps.labelPositionLeft,
    name,
    onBlur,
    onChange,
    onFocus,
    screenreaderText,
    size = defaultProps.size,
    title,
    toggleButton = defaultProps.toggleButton,
    variant,
    checkboxContainerWidth,
    checkboxVerticalPosition = defaultProps.checkboxVerticalPosition
  } = props;

  // Provides a fallback argument so Formik's useField doesn't complain
  const fieldArgs = { ...props, name: props.name || 'fallback' };
  const [field] = useField( fieldArgs );
  const labelPosition = labelPositionLeft ? 'left' : 'right';
  const derievedVariant = getCheckboxVariant( { variant, toggleButton } );

  const onChangeHandler = useCallback(
    utils.composeChangeHandler( { dataCaptureData, checked }, { fieldOnChange: field.onChange, triggerDatacaptureEvent, onChange } )
    , [field, checked] );

  const isChecked = !!( checked || defaultChecked );

  // Error or ariaDescribedBy We set that Id for aria-describedby
  let ariaId = null;
  if( error && name ){
    ariaId = `${name}-error`;
  }
  else if( error && id ){
    ariaId = `${id}-error`;
  }
  else if( ariaDescribedBy ){
    ariaId = ariaDescribedBy;
  }

  return (
    <div className='CheckboxToggle'>
      <PalCheckbox
        ariaDescribedBy={ ariaDescribedBy }
        ariaLabel={ ariaLabel }
        ariaLabelledBy={ ariaLabelledBy }
        checked={ isChecked }
        className={ className }
        count={ count }
        description={ description }
        disabled={ disabled }
        id={ id }
        inputRef={ ref }
        isFullyClickable={ isFullyClickable }
        labelPosition={ labelPosition }
        onBlur={ onBlur }
        onChange={ onChangeHandler }
        onFocus={ onFocus }
        screenreaderText={ screenreaderText }
        size={ size }
        title={ title }
        variant={ derievedVariant }
        checkboxContainerWidth={ checkboxContainerWidth }
        checkboxVerticalPosition={ checkboxVerticalPosition }
      >
        { children }
      </PalCheckbox>
      {
        error &&
        <ResponseMessages
          id={ ariaId }
          message={ error }
          messageType={ 'error' }
        />
      }
    </div>
  );
} );


/**
 * Function to format legacy toggleButton props to palette variant props.
 * We prefer palette variant props when they are present.
 * @method
 * @param {Object} data data arguments
 * @param {Object} data.variant passed variant value
 * @param {Object} data.toggleButton passed toggleButton value
 * @returns { String } string for checkbox variant
 */
export const getCheckboxVariant = ( data ) => {
  const { variant, toggleButton } = data;

  if( variant ){
    return variant;
  }
  else if( toggleButton ){
    return 'toggle';
  }
  else {
    return 'default';
  }
};

/**
 * Handles onChange for Palette Checkbox
 * @method
 * @param {Object} data data arguments
 * @param {object} data.dataCaptureData data capture data
 * @param {object} data.checked checkbox checked value
 * @param {Object} methods methods arguments
 * @param {Object} methods.fieldOnChange Field onChange callback
 * @param {function} methods.triggerDatacaptureEvent Caller's datacapture when quantity got changes
 * @param {Object} methods.fieldOnChange Caller's onChange callback
 */
export const composeChangeHandler = ( data, methods ) => ( e ) => {
  const { dataCaptureData, checked } = data;
  const { fieldOnChange, triggerDatacaptureEvent, onChange } = methods;
  if( !fieldOnChange && !triggerDatacaptureEvent && !onChange ){
    return;
  }
  fieldOnChange && fieldOnChange( e );
  triggerDatacaptureEvent && triggerDatacaptureEvent( dataCaptureData );
  onChange && onChange( checked );
};

/**
 * Property type definitions
 * @typedef CheckboxToggleProps
 * @type {object}
 * @property {boolean} disabled - Sets the user behavior of toggle button
 * @property {string} name - Sets the name attribute which is required field
 * @property {string} id - Sets id attribute for toggle button
 * @property {boolean} labelPositionLeft - Sets the position of the label
 * @property {boolean} checked - Sets the state of toggle button
 * @property {boolean} toggleButton - Sets checkbox or toggle button display, the prop is deprecated now and will be in effect if variant prop is null
 * @property {string} variant - Sets checkbox variant as default or toggle
 * @property {string} error - Sets the exclamationcircle svg when there is error
 * @property {string} ariaDescribedBy - Sets the aria-describedby ID on the input for an external reference
 * @property {string} ariaLabelledBy - Sets the aria-labelledby ID on the input for an external reference
 * @property {boolean} isFullyClickable - Sets whether the entire checkbox area is clickable
 * @property {string} size - Sets the size of the checkbox toggle
 * @property {function} onFocus - Callback function triggered when checkbox receives focus
 * @property {function} onBlur - Callback function triggered when checkbox loses focus
 * @property {string} ariaLabel - Sets the aria-label attribute for accessibility
 * @property {string} className - Sets additional CSS class names for styling
 * @property {string} title - Sets the title attribute for the checkbox
 * @property {string} description - Sets the description text for the checkbox
 * @property {number} count - Sets the count value to display with the checkbox
 * @property {string} screenreaderText - Sets additional text for screen readers
 * @property {string} checkboxContainerWidth - Sets the width of the container of the Checkbox
 * @property {string} checkboxVerticalPosition - Sets the vertical position of the Checkbox relative to the label (options: bottom, center, top)
 */
export const propTypes = {
  /** Sets the checkboxToggle to disabled mode if true */
  disabled: PropTypes.bool,
  /** Sets the checkboxToggle's name */
  name: PropTypes.string.isRequired,
  /** Sets the checkboxToggle's id */
  id: PropTypes.oneOfType( [PropTypes.string, PropTypes.number] ),
  /** Sets the label to left side if true */
  labelPositionLeft: PropTypes.bool,
  /** Sets the checkboxToggle as selected when true */
  checked: PropTypes.bool,
  /** Sets the checkboxToggle as toggle button if true and as checkbox when false, the prop is deprecated now and will be in effect if variant prop is null*/
  toggleButton: PropTypes.bool,
  /** Sets the checkboxToggle as variant as 'default' or 'toggle'*/
  variant: PropTypes.string,
  /** Sets the error message when present*/
  error: PropTypes.string,
  /** Sets the aria-describedby ID on the input for an external reference */
  ariaDescribedBy: PropTypes.string,
  /** Sets the aria-labelledby ID on the input for an external reference */
  ariaLabelledBy: PropTypes.string,
  /** Sets whether the entire checkbox area is clickable */
  isFullyClickable: PropTypes.bool,
  /** Sets the size of the checkbox toggle */
  size: PropTypes.string,
  /** Callback function triggered when checkbox receives focus */
  onFocus: PropTypes.func,
  /** Callback function triggered when checkbox loses focus */
  onBlur: PropTypes.func,
  /** Sets the aria-label attribute for accessibility */
  ariaLabel: PropTypes.string,
  /** Sets additional CSS class names for styling */
  className: PropTypes.string,
  /** Sets the title attribute for the checkbox */
  title: PropTypes.string,
  /** Sets the description text for the checkbox */
  description: PropTypes.string,
  /** Sets the count value to display with the checkbox */
  count: PropTypes.number,
  /** Sets additional text for screen readers */
  screenreaderText: PropTypes.string,
  /** Sets the text and count content */
  children: PropTypes.node,
  /** Sets the width of the container of the Checkbox */
  checkboxContainerWidth: PropTypes.string,
  /** Sets the vertical position of the Checkbox relative to the label (options: bottom, center, top) */
  checkboxVerticalPosition: PropTypes.string
};

/**
 * Default values for passed properties
 * @type {object}
 * @property {boolean} labelPositionLeft=false - Sets the position of label to left side.
 * @property {boolean} toggleButton=false - Sets the component behavior if true to toggleButton else to checkbox
 * @property {boolean} isFullyClickable=true - Sets whether the entire checkbox area is clickable
 * @property {string} size='default' - Sets the size of the checkbox toggle
 */
export const defaultProps = {
  labelPositionLeft: false,
  toggleButton: false,
  isFullyClickable: true,
  size: 'default',
  checkboxVerticalPosition: 'center'
};

CheckboxToggle.displayName = 'CheckboxToggle';
CheckboxToggle.propTypes = propTypes;
CheckboxToggle.defaultProps = defaultProps;

export default CheckboxToggle;
